import _ from 'lodash';
import React from 'react';

import {
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  Replay
} from '@mui/icons-material';
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Stack, styled, TextField
} from '@mui/material';
import update from 'immutability-helper';



const ListContainerBox = styled(Box)({
  flex: 1,
  alignSelf: 'stretch'
});

const StyledList = styled(List)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  background: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  minHeight: '300px',
  maxHeight: '550px',
  overflowY: 'scroll'
}));



export type IValue = _.Dictionary<boolean>;

export interface IItem {
  id: string;
  primaryText: string;
  secondaryText: string;
  disabled?: boolean;
}

export interface IProps {
  defaultId2IsSelected?: IValue;
  id2IsSelected: IValue;
  allItems: IItem[];
  loading: boolean;

  onChange: (id2IsSelected: IValue) => void;
}

export const FC: React.FC<IProps> = props => {
  const [searchText, setSearchText] = React.useState('');

  const notDisabledItems = React.useMemo(
    () => props.allItems.filter(item => !item.disabled),
    [props.allItems]
  );

  const notSelectedItems = React.useMemo(
    () => props.allItems.filter(item => !props.id2IsSelected[item.id]),
    [
      props.allItems,
      props.id2IsSelected
    ]
  );

  const selectedItems = React.useMemo(
    () => props.allItems.filter(item => props.id2IsSelected[item.id]),
    [
      props.allItems,
      props.id2IsSelected
    ]
  );


  const handleClickReset = React.useCallback(
    () => {
      props.onChange(props.defaultId2IsSelected ?? {});
    },
    [props]
  );



  const handleClickRemoveAll = React.useCallback(
    () => {
      const id2Item = _.keyBy(notDisabledItems, item => item.id);
      props.onChange(_.mapValues(id2Item, () => false));
    },
    [notDisabledItems, props]
  );



  const handleClickAddAll = React.useCallback(
    () => {
      const id2Item = _.keyBy(notDisabledItems.filter(i => !i.disabled), item => item.id);
      props.onChange(_.mapValues(id2Item, () => true));
    },
    [notDisabledItems, props]
  );



  const handleClickItem = React.useCallback(
    (itemId: string) => {
      const newValue = update(props.id2IsSelected, {
        [itemId]: {
          $set: !props.id2IsSelected[itemId]
        }
      });
      props.onChange(newValue);
    },
    [props]
  );

  return (
    <>
      {props.loading ? (
        <Skeleton />
      ) : (
        <TextField
          label="Search"
          value={searchText}
          onChange={ev => setSearchText(ev.target.value)} />
      )}

      <Stack
        justifyContent="space-between"
        alignItems="center"
        direction="row"
        spacing={2}>
        <ListComponent
          title="Not Selected"
          totalCount={props.allItems.length}
          items={notSelectedItems}
          loading={props.loading}
          searchText={searchText}
          onClick={handleClickItem} />

        <Stack gap={2}>
          <IconButton
            disabled={props.loading}
            onClick={handleClickReset}>
            <Replay />
          </IconButton>

          <IconButton
            onClick={handleClickRemoveAll}
            disabled={_.isEmpty(selectedItems)}>
            <KeyboardDoubleArrowLeft />
          </IconButton>

          <IconButton
            onClick={handleClickAddAll}
            disabled={_.isEmpty(notSelectedItems)}>
            <KeyboardDoubleArrowRight />
          </IconButton>
        </Stack>

        <ListComponent
          title="Selected"
          totalCount={props.allItems.length}
          items={selectedItems}
          loading={props.loading}
          searchText={searchText}
          onClick={handleClickItem} />
      </Stack>
    </>
  );
};



const ListComponent = ({
  title, loading, totalCount, searchText, items, onClick
}: {
  title: string;
  loading: boolean;
  totalCount: number;
  searchText: string;
  items: IItem[];
  onClick: (itemId: string) => void;
}) => {
  return (
    <ListContainerBox>
      <ListItemText
        primary={loading ? <Skeleton /> : title}
        secondary={loading ? <Skeleton /> : `${items.length} / ${totalCount}`} />
      <StyledList
        dense
        disablePadding>
        {loading && _.times(5).map(i => (
          <ListItem key={i}>
            <ListItemText
              primary={<Skeleton />}
              secondary={<Skeleton />} />
          </ListItem>
        ))}

        {items.filter(item => __isMatch(item, searchText)).map(item => (
          <ListItem
            key={item.id}
            sx={{
              cursor: item.disabled ? undefined : 'pointer',
              opacity: item.disabled ? 0.5 : 1,
              userSelect: 'none'
            }}
            onClick={item.disabled ? undefined : _.partial(onClick, item.id)}>
            <ListItemText
              primary={item.primaryText}
              secondary={item.secondaryText} />
          </ListItem>
        ))}
      </StyledList>
    </ListContainerBox>
  );
};



function __isMatch(item: IItem, searchText: string) {
  const loweredSku = _.toLower(item.primaryText);
  const loweredName = _.toLower(item.secondaryText);
  const loweredSearchText = _.toLower(searchText);

  return _.includes(loweredSku, loweredSearchText) || _.includes(loweredName, loweredSearchText);
}




export function useTransferListState(defaultSelectedIds: string[] = []) {
  const defaultValue = React.useMemo(
    () => _.mapValues(_.keyBy(defaultSelectedIds, id => id), () => true),
    [defaultSelectedIds]
  );

  const [
    value,
    setValue
  ] = React.useState<IValue>(defaultValue);

  React.useEffect(() => {
    if (_.isEmpty(defaultSelectedIds) && _.isEmpty(defaultValue)) {
      return;
    }
    setValue(defaultValue);
  }, [
    defaultSelectedIds,
    defaultValue
  ]);

  return {
    defaultId2IsSelected: defaultValue,
    id2IsSelected: value,
    onChange: setValue
  };
}
